import type React from 'react';

import { CheckmarkIcon } from './icons';
import {
	getItemStyles,
	getPillStyles,
	itemContentStyles,
	itemDescriptionStyles,
	itemIconContainerStyles,
	itemTitleRowStyles,
	itemTitleStyles,
} from './styled';

interface ConsentItemProps {
	icon: React.ReactNode;
	title: string;
	description: string;
	required: boolean;
	isChecked: boolean;
	onToggle: () => void;
	delay: number;
}

const indicatorStyles = (isChecked: boolean): React.CSSProperties => ({
	width: '26px',
	height: '26px',
	minWidth: '26px',
	borderRadius: '7px',
	marginTop: '2px',
	border: isChecked ? 'none' : '2px solid var(--color-border-strong)',
	backgroundColor: isChecked ? 'var(--color-success)' : 'transparent',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	transition: 'all 200ms cubic-bezier(0.22, 1, 0.36, 1)',
	animation: isChecked ? 'checkPop 300ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
	flexShrink: 0,
});

const ConsentItem: React.FC<ConsentItemProps> = ({ icon, title, description, required, isChecked, onToggle, delay }) => (
	<div
		role='checkbox'
		aria-checked={isChecked}
		aria-label={`${title}${required ? ' (required)' : ' (optional)'}`}
		tabIndex={0}
		style={{
			...getItemStyles(isChecked),
			animation: `checklistSlideUp 400ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms both`,
		}}
		onClick={onToggle}
		onKeyDown={(e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				onToggle();
			}
		}}
	>
		<div style={itemIconContainerStyles(isChecked)}>{icon}</div>

		<div style={itemContentStyles}>
			<div style={itemTitleRowStyles}>
				<p style={itemTitleStyles}>{title}</p>
				<span style={getPillStyles(required)}>{required ? 'Required' : 'Optional'}</span>
			</div>
			<p style={itemDescriptionStyles}>{description}</p>
		</div>

		<div style={indicatorStyles(isChecked)}>{isChecked && <CheckmarkIcon />}</div>
	</div>
);

export default ConsentItem;
